import { useState, Fragment } from 'react';
import intl from 'react-intl-universal';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid2';
import Modal from '@mui/material/Modal';
import { Card, Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// self defined
import api from 'utils/api';
import { updateArrayItemsByIds } from 'utils/javascript';
import { Actions, StyledButton } from 'shared/modals/Styles';
import { KENSHIN_API } from 'shared/constants/apis';
import { IssueStatusKenshin } from 'shared/constants/kenshin/issues';
import DoctorSelect from './AssigneesReporter';
import { zIndexValues } from 'utils/styles';

const propTypes = {
  isOpened: PropTypes.bool,
  closeModal: PropTypes.func,
  selectedCases: PropTypes.array.isRequired,
  doctors: PropTypes.array.isRequired,
  updateLocalCase: PropTypes.func,
  setBatchEditModalIsOpen: PropTypes.func,
  updateLocalProjectIssues: PropTypes.func,
  issue: PropTypes.any,
  updateIssue: PropTypes.func,
};

const KenshinCaseBatchEditModal = ({
  isOpened,
  closeModal,
  selectedCases,
  doctors,
  updateLocalCase,
  setBatchEditModalIsOpen,
  updateLocalProjectIssues,
  issue,
  updateIssue,
}) => {
  let allCases = issue.cases;

  const firstCase = selectedCases[0];
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalUpdateData, setConfirmModalUpdateData] = useState();

  const batchUpdateCases = (updatedFields, selectedCases) => {
    api.optimisticBatchUpdateKenshinCases(`${KENSHIN_API.CASES_BATCH_UPDATE}`, {
      selectedCases,
      updatedFields,
      setLocalData: (caseId, fields) => {
        // this is the local action that is being executed
        updateLocalCase(caseId, fields);
      },
    });
  };

  const handleConfirmModalYesButtonClick = async () => {
    await batchUpdateCases(confirmModalUpdateData, selectedCases);

    // this code updates the case info of this issue in the `project` of kanban root;
    const ids = selectedCases.map((c) => c.id);
    const updatedCasesArray = updateArrayItemsByIds(
      allCases,
      ids,
      confirmModalUpdateData
    );

    updateLocalProjectIssues(issue.id, {
      cases: updatedCasesArray,
    });

    // If anything about the cases changes when issue is in NEWTASK,
    // we move the issue from NEWTASK to the next status;
    const newProgress =
      issue.progress === IssueStatusKenshin.NEWTASK
        ? IssueStatusKenshin.DOCTORASSIGNED
        : issue.progress;
    await updateIssue({
      progress: newProgress,
    });

    allCases = updatedCasesArray;
    setConfirmModalOpen(false);
    setBatchEditModalIsOpen(false);
  };

  const handleConfirmModalNoButtonClick = () => {
    setConfirmModalOpen(false);
  };

  return (
    <Fragment>
      <Modal
        data-testid="kenshinCaseBatchEditModal"
        disableEscapeKeyDown
        open={isOpened}
        onClose={closeModal}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: zIndexValues.modal2,
          overflow: 'visible',
        }}>
        <Card>
          <Grid container justifyContent="flex-end">
            <Grid>
              <IconButton
                onClick={closeModal}
                sx={{ paddingRight: 1, paddingTop: 1 }}>
                <CloseIcon fontSize="medium" />
              </IconButton>
            </Grid>
          </Grid>

          <Box m={4}>
            <Typography variant="h5" component="h2" data-testid="title-text">
              {intl
                .get(
                  'kenshin_kanban_case_batch_edit_modal_text_already_selected'
                )
                .replace('{arg1}', `${selectedCases.length}`)}
            </Typography>
            <Typography
              variant="h6"
              component="h2"
              style={{ marginTop: '1px' }}>
              {intl.get(
                'kenshin_kanban_case_batch_edit_modal_text_select_doctor_please'
              )}
            </Typography>

            <Grid container justifyContent="center" alignItems="center">
              <Grid>
                <DoctorSelect
                  firstCase={firstCase}
                  doctors={doctors}
                  // setConfirmModalInfo={setConfirmModalInfo}
                  setConfirmModalUpdateData={setConfirmModalUpdateData}
                  setConfirmModalOpen={setConfirmModalOpen}
                />
              </Grid>
            </Grid>
          </Box>
        </Card>
      </Modal>

      {confirmModalOpen && (
        <Modal
          data-testid="confirm-modal"
          disableEscapeKeyDown
          open={confirmModalOpen}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '50vw',
            zIndex: zIndexValues.modal3,
          }}>
          <Card>
            <Box py={3} px={3} mt={3} mx={3}>
              <Grid container justifyContent="center" mb={5}>
                <Grid size={12}>
                  <Typography variant="body1">
                    {intl
                      .get(
                        'kenshin_kanban_case_batch_edit_modal_text_hint_next'
                      )
                      .replace('{arg1}', `${selectedCases.length}`)}
                  </Typography>
                </Grid>
              </Grid>
              <Actions>
                <StyledButton
                  data-testid="confirm-modal-button-yes"
                  variant="primary"
                  onClick={handleConfirmModalYesButtonClick}>
                  {intl.get('kanban_confirm_popup_window_button_yes')}
                </StyledButton>
                <StyledButton
                  data-testid="confirm-modal-button-no"
                  hollow
                  onClick={handleConfirmModalNoButtonClick}>
                  {intl.get('kanban_confirm_popup_window_button_no')}
                </StyledButton>
              </Actions>
            </Box>
          </Card>
        </Modal>
      )}
    </Fragment>
  );
};

KenshinCaseBatchEditModal.propTypes = propTypes;

export default KenshinCaseBatchEditModal;
